import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import IContextDocument from "../../model/document/IContextDocument";
import UserRightScopeEnum from "../../enums/UserRightScopeEnum";
import SceneObjectTypeEnum from "../../model/sceneObjectData/SceneObjectTypeEnum";
import DocumentEventType from "../../enums/DocumentEventType";

interface DocumentHeaderProps {
    contextDoc: IContextDocument;
    index: number;
    isActive: boolean;
    onClick: (doc: IContextDocument) => void;
}

const DocumentHeaderComponent = ({ contextDoc, index, isActive, onClick }: DocumentHeaderProps) => {
    const { t } = useTranslation();
    const [, setUpdateCount] = useState(0);

    useEffect(() => {
        const handleUpdate = () => {
            setUpdateCount((prev) => prev + 1);
        };

        const document = contextDoc.document;
        document.addEventListener(DocumentEventType.ContentsChanged, handleUpdate);

        return () => {
            document.removeEventListener(DocumentEventType.ContentsChanged, handleUpdate);
        };
    }, [contextDoc.document]);

    const scopeLabel = (scope: UserRightScopeEnum): string => {
        switch (scope) {
            case UserRightScopeEnum.All:      return t("document.userRights.scopeAll");
            case UserRightScopeEnum.Group:    return t("document.userRights.scopeGroup");
            case UserRightScopeEnum.Own:      return t("document.userRights.scopeOwn");
            case UserRightScopeEnum.NoRights: return t("document.userRights.scopeNoRights");
        }
    };

    const userRight = contextDoc.document.userRight;
    const tooltip = [
        t("document.userRights.tooltip") + ":",
        `${t("document.userRights.create")}: ${userRight.create ? t("document.userRights.scopeAll") : t("document.userRights.scopeNoRights")}`,
        `${t("document.userRights.read")}: ${scopeLabel(userRight.read)}`,
        `${t("document.userRights.update")}: ${scopeLabel(userRight.update)}`,
        `${t("document.userRights.delete")}: ${scopeLabel(userRight.delete)}`,
        "",
        `UUID: ${contextDoc.document.documentUuid}`,
    ].join("\n");

    const counts = contextDoc.document.sceneObjects.reduce((acc, obj) => {
        const type = obj.data.type;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    return (
        <span
            className={`document-manager__item${isActive ? " document-manager__item--active" : ""}`}
            onClick={() => onClick(contextDoc)}
            title={tooltip}
        >
            <div className="document-manager__item-name">
                {contextDoc.document.name || `Document ${index + 1}`}
            </div>
            <div className="document-manager__item-summary">
                {counts[SceneObjectTypeEnum.LengthDimension] > 0 && (
                    <div>- {counts[SceneObjectTypeEnum.LengthDimension]} length dimensions</div>
                )}
                {counts[SceneObjectTypeEnum.TextAnnotation] > 0 && (
                    <div>- {counts[SceneObjectTypeEnum.TextAnnotation]} text annotations</div>
                )}
                {counts[SceneObjectTypeEnum.ChecklistItem] > 0 && (
                    <div>- {counts[SceneObjectTypeEnum.ChecklistItem]} checklist items</div>
                )}
                {counts[SceneObjectTypeEnum.AreaAnnotation] > 0 && (
                    <div>- {counts[SceneObjectTypeEnum.AreaAnnotation]} areas</div>
                )}
                {counts[SceneObjectTypeEnum.RectangleAnnotation] > 0 && (
                    <div>- {counts[SceneObjectTypeEnum.RectangleAnnotation]} rectangles</div>
                )}
                {counts[SceneObjectTypeEnum.CircleAnnotation] > 0 && (
                    <div>- {counts[SceneObjectTypeEnum.CircleAnnotation]} circles</div>
                )}
            </div>
        </span>
    );
};

export default DocumentHeaderComponent;
